/**
 * useLayerCatalog Hook
 *
 * React hook that replaces the legacy layerAdapter.js IIFE.
 * Provides layer catalog fetching, caching, and Leaflet layer creation.
 *
 * Features:
 * - Fetches layer definitions from /layers/catalog API
 * - Caches layer definitions with configurable TTL
 * - Creates Leaflet layer instances from definitions
 * - Tracks WMS layers by their underlying layer name
 * - Maintains backward compatibility with window.LayerAdapter
 */
import { useCallback, useRef, useState } from 'react';
import { applyWmsColorOverride } from '../../../utils/wmsStyleOverrides';
// Module-level caches (shared across hook instances)
let catalogCache = null;
const layerCache = new Map();
const wmsLayerCache = new Map();
/**
 * Fetch layer definitions from the API
 */
async function fetchLayerDefinitions(forceRefresh = false) {
    if (!forceRefresh && catalogCache !== null) {
        console.log('useLayerCatalog: Returning cached catalog');
        return catalogCache;
    }
    try {
        console.log('useLayerCatalog: Fetching catalog from /layers/catalog');
        const response = await fetch('/layers/catalog', {
            credentials: 'include',
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache',
            },
        });
        if (!response.ok) {
            console.error(`useLayerCatalog: Failed to fetch, status: ${response.status}`);
            throw new Error('Failed to fetch layer definitions');
        }
        catalogCache = await response.json();
        console.log('useLayerCatalog: Fetched catalog', {
            groups: Object.keys(catalogCache?.layersByGroup || {}),
            layerCount: Object.values(catalogCache?.layers || {}).reduce((acc, category) => acc + Object.keys(category).length, 0),
        });
        return catalogCache;
    }
    catch (error) {
        console.error('useLayerCatalog: Error fetching catalog:', error);
        return null;
    }
}
/**
 * Create a Leaflet layer from a layer definition
 */
function createLeafletLayer(layerId, layerDef, cqlFilter = null) {
    const L = window.L;
    if (!L) {
        console.error('useLayerCatalog: Leaflet (L) not available');
        return null;
    }
    console.log(`useLayerCatalog: Creating layer "${layerId}" (type: ${layerDef.type})`);
    // Create cache key
    const cacheKey = cqlFilter ? `${layerId}::${cqlFilter}` : layerId;
    // Return from cache if available
    if (layerCache.has(cacheKey)) {
        console.log(`useLayerCatalog: Returning cached layer "${cacheKey}"`);
        return layerCache.get(cacheKey);
    }
    let layer = null;
    switch (layerDef.type) {
        case 'tile':
            console.log(`useLayerCatalog: Creating tile layer "${layerId}"`);
            layer = L.tileLayer(layerDef.url_template || layerDef.url, layerDef.options);
            break;
        case 'wms': {
            console.log(`useLayerCatalog: Creating WMS layer "${layerId}"`);
            // Clone options to avoid modifying the original
            const options = JSON.parse(JSON.stringify(layerDef.options));
            // Add cql_filter if provided
            if (cqlFilter) {
                const baseCqlFilter = options.cql_filter || '';
                options.cql_filter = baseCqlFilter ? `${baseCqlFilter} AND ${cqlFilter}` : cqlFilter;
                console.log(`useLayerCatalog: Added CQL filter to "${layerId}": ${options.cql_filter}`);
            }
            const overrideApplied = applyWmsColorOverride(options, {
                layerId,
                layerKey: layerDef.layer_key,
                name: layerDef.name,
                displayName: layerDef.display_name,
                wmsName: typeof options.layers === 'string' ? options.layers : undefined,
            });
            if (overrideApplied) {
                console.log(`useLayerCatalog: Applied color override to "${layerId}"`);
            }
            layer = L.tileLayer.wms(layerDef.url, options);
            // Track WMS layers by their underlying WMS layer name
            if (options.layers && layer) {
                const existing = wmsLayerCache.get(options.layers) || [];
                existing.push(layer);
                wmsLayerCache.set(options.layers, existing);
                console.log(`useLayerCatalog: Tracked WMS layer "${layerId}" by name "${options.layers}"`);
            }
            // Register with WmsFeatureInfoHandler if feature_info_config is enabled
            // Skip layers handled by /geo/nearest (structures, conduits, BFT)
            const geoNearestLayers = ['cfn:pia_structure', 'cfn:phys_node', 'cfn:pia_duct', 'cfn:bft'];
            const isGeoNearestLayer = geoNearestLayers.includes(options.layers);
            if (layerDef.feature_info_config?.enabled && layer && !isGeoNearestLayer) {
                const handler = window.wmsFeatureInfoHandler;
                if (handler) {
                    handler.registerLayer(layerId, layer, layerDef.feature_info_config, { layers: options.layers, cql_filter: options.cql_filter }, layerDef.name || layerDef.display_name || layerId);
                    console.log(`useLayerCatalog: Registered "${layerId}" with WmsFeatureInfoHandler`);
                }
            }
            else if (isGeoNearestLayer) {
                console.log(`useLayerCatalog: Skipping WmsFeatureInfoHandler for "${layerId}" (handled by /geo/nearest)`);
            }
            break;
        }
        case 'pbf': {
            console.log(`useLayerCatalog: Creating vector tile layer "${layerId}"`);
            if (!L.vectorGrid?.protobuf) {
                console.error('useLayerCatalog: L.vectorGrid.protobuf not available');
                return null;
            }
            const vectorTileOptions = {
                rendererFactory: L.canvas?.tile,
                minZoom: layerDef.options.minZoom,
                maxZoom: layerDef.options.maxZoom || 23,
                interactive: true,
                continuousWorld: true,
                getFeatureId: (feature) => feature.properties[layerDef.options.getFeatureId],
                vectorTileLayerStyles: {
                    uprns: (properties, zoom) => {
                        const min_outer = 4;
                        const min_inner = 2;
                        const outer = Math.max(Math.min(10, 10 + (zoom - 19) * 2), min_outer);
                        const inner = Math.max(Math.min(7, 7 + (zoom - 19) * 2), min_inner);
                        const style = {
                            Commercial: [
                                { radius: outer, color: '#fac300' },
                                { radius: inner, color: '#101bef', fill: true },
                            ],
                            Residential: [
                                { radius: outer, color: '#fac300' },
                                { radius: inner, color: '#008000', fill: true },
                            ],
                        };
                        return style[properties.classification];
                    },
                },
            };
            layer = L.vectorGrid.protobuf(layerDef.url_template || layerDef.url, vectorTileOptions);
            // Add click handler for vector tiles
            if (layer && typeof layer.on === 'function') {
                layer.on('click', (e) => {
                    console.log('Vector tile clicked:', e);
                    const content = ['<table>'];
                    for (const key in e.layer.properties) {
                        content.push(`<tr><th>${key}</th><td>${e.layer.properties[key]}</td></tr>`);
                    }
                    content.push('</table>');
                    L.popup?.()
                        .setContent(content.join('\n'))
                        .setLatLng(e.latlng)
                        .openOn(layer._map);
                    // Highlight the clicked feature
                    if (window.layerHighlight) {
                        layer.resetFeatureStyle?.(window.layerHighlight);
                    }
                    window.layerHighlight = e.layer.properties[layerDef.options.getFeatureId];
                    layer.setFeatureStyle?.(window.layerHighlight, {
                        weight: 2,
                        color: 'red',
                        opacity: 1,
                        fillColor: 'red',
                        fill: true,
                        radius: 6,
                        fillOpacity: 1,
                    });
                    L.DomEvent?.stop(e);
                });
            }
            break;
        }
        case 'geojson': {
            console.log(`useLayerCatalog: Creating GeoJSON layer "${layerId}"`);
            if (!L.GeoJSON?.AJAX) {
                console.error('useLayerCatalog: L.GeoJSON.AJAX not available');
                return null;
            }
            const geojsonOptions = {
                onEachFeature: (feature, featureLayer) => {
                    let txt = '<table>';
                    for (const key in feature.properties) {
                        txt += `<tr><th>${key}</th><td>${feature.properties[key]}</td></tr>`;
                    }
                    txt += '</table>';
                    featureLayer.bindPopup(txt);
                },
            };
            // Add pointToLayer function if specified
            if (layerDef.style?.pointToLayer) {
                const styleFunc = window.pointToLayerRegistry?.[layerDef.style.pointToLayer];
                if (styleFunc) {
                    geojsonOptions.pointToLayer = styleFunc;
                    console.log(`useLayerCatalog: Applied pointToLayer "${layerDef.style.pointToLayer}"`);
                }
                else {
                    console.warn(`useLayerCatalog: pointToLayer "${layerDef.style.pointToLayer}" not found`);
                }
            }
            layer = new L.GeoJSON.AJAX(layerDef.url, geojsonOptions);
            break;
        }
        default:
            // Handle UPRN dataset layers (they report type 'wms' but have special key prefix)
            if (layerId.startsWith('uprn_dataset:')) {
                console.log(`useLayerCatalog: Creating UPRN dataset layer "${layerId}"`);
                layer = L.tileLayer.wms(layerDef.url, layerDef.options);
            }
            else {
                console.warn(`useLayerCatalog: Unknown layer type: ${layerDef.type} for "${layerId}"`);
                return null;
            }
    }
    // Attach layer definition for later reference
    if (layer) {
        layer._layerDefinition = layerDef;
        // Mark UPRN dataset layers for special handling
        if (layerId.startsWith('uprn_dataset:')) {
            layer._isUprnDataset = true;
            layer._uprnDatasetId = layerDef.dataset_id;
        }
    }
    // Cache the layer
    if (layer) {
        layerCache.set(cacheKey, layer);
        console.log(`useLayerCatalog: Cached layer "${cacheKey}"`);
    }
    return layer;
}
/**
 * Get a layer by ID
 */
async function getLayer(layerId, cqlFilter = null) {
    console.log(`useLayerCatalog: getLayer("${layerId}"${cqlFilter ? `, filter: ${cqlFilter}` : ''})`);
    const definitions = await fetchLayerDefinitions();
    if (!definitions) {
        console.error(`useLayerCatalog: No definitions available for "${layerId}"`);
        return null;
    }
    // Look in all layer categories
    for (const category of Object.keys(definitions.layers)) {
        const categoryLayers = definitions.layers[category];
        if (categoryLayers && categoryLayers[layerId]) {
            console.log(`useLayerCatalog: Found "${layerId}" in category "${category}"`);
            return createLeafletLayer(layerId, categoryLayers[layerId], cqlFilter);
        }
    }
    console.warn(`useLayerCatalog: Layer "${layerId}" not found`);
    return null;
}
/**
 * Get layers by group ID
 */
async function getLayersByGroup(groupId) {
    console.log(`useLayerCatalog: getLayersByGroup("${groupId}")`);
    const definitions = await fetchLayerDefinitions();
    if (!definitions) {
        console.error(`useLayerCatalog: No definitions for group "${groupId}"`);
        return {};
    }
    const groupLayers = definitions.layersByGroup[groupId];
    if (!groupLayers) {
        console.warn(`useLayerCatalog: Group "${groupId}" not found`);
        return {};
    }
    console.log(`useLayerCatalog: Found ${Object.keys(groupLayers).length} layers in group "${groupId}"`);
    const result = {};
    for (const layerId of Object.keys(groupLayers)) {
        console.log(`useLayerCatalog: Getting layer "${layerId}" for group "${groupId}"`);
        const layer = await getLayer(layerId);
        if (layer) {
            const layerDef = groupLayers[layerId] || layer._layerDefinition;
            const baseName = layerDef?.display_name || layerDef?.name || layerId;
            // Ensure unique keys
            let uniqueName = baseName;
            let counter = 2;
            while (Object.prototype.hasOwnProperty.call(result, uniqueName)) {
                uniqueName = `${baseName} (${counter++})`;
            }
            result[uniqueName] = layer;
        }
        else {
            console.warn(`useLayerCatalog: Failed to get layer "${layerId}" for group "${groupId}"`);
        }
    }
    console.log(`useLayerCatalog: Returning ${Object.keys(result).length} layers for group "${groupId}"`);
    return result;
}
/**
 * Get a cached layer by ID
 */
function getCachedLayer(layerId) {
    return layerCache.get(layerId) || null;
}
/**
 * Get all WMS layers with a specific WMS layer name
 */
function getWmsLayersByName(wmsLayerName) {
    return wmsLayerCache.get(wmsLayerName) || [];
}
/**
 * Clear all caches
 */
function refreshDefinitions() {
    console.log('useLayerCatalog: Clearing caches');
    catalogCache = null;
    layerCache.clear();
    wmsLayerCache.clear();
}
/**
 * React hook for layer catalog management
 */
export function useLayerCatalog(options = {}) {
    const { autoFetch = false } = options;
    const [catalog, setCatalog] = useState(catalogCache);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const hasFetched = useRef(false);
    const fetchCatalog = useCallback(async (forceRefresh = false) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await fetchLayerDefinitions(forceRefresh);
            setCatalog(result);
            return result;
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to fetch catalog';
            setError(message);
            return null;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const refresh = useCallback(() => {
        refreshDefinitions();
        setCatalog(null);
        fetchCatalog(true);
    }, [fetchCatalog]);
    // Auto-fetch on mount if enabled
    if (autoFetch && !hasFetched.current && !catalog) {
        hasFetched.current = true;
        fetchCatalog();
    }
    return {
        catalog,
        isLoading,
        error,
        fetchCatalog,
        getLayer,
        getLayersByGroup,
        getCachedLayer,
        getWmsLayersByName,
        refresh,
    };
}
/**
 * Create the LayerAdapter API for backward compatibility with legacy code.
 * This is exposed on window.LayerAdapter to maintain compatibility.
 */
export function createLayerAdapterAPI() {
    return {
        getLayer,
        getLayersByGroup,
        getCachedLayer,
        getWmsLayersByName,
        refreshDefinitions,
        getLayerDefinitions: fetchLayerDefinitions,
    };
}
/**
 * Initialize window.LayerAdapter for backward compatibility.
 * Call this once during app initialization.
 */
export function initializeLayerAdapter() {
    if (typeof window !== 'undefined') {
        window.LayerAdapter = createLayerAdapterAPI();
        console.log('useLayerCatalog: Initialized window.LayerAdapter');
    }
}
export default useLayerCatalog;
